import React, { useEffect, useState } from "react";
import { Button, Card, CardActions, CardContent, CardHeader, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, WithStyles, withStyles } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { IceAdmin, IceAdminLoginType } from "../../model/IceAdmin";
import { ApiBackend } from "../../providers/apibackend";
import withToaster, { IToasterContext } from "../Common/Toaster";
import { vasaloppetDateTimeGetterFormatter } from "../../utilities/date";

const ManageIceAdmins = (props: WithStyles) => {
    const [loading, setLoading] = useState(true);
    const [admins, setAdmins] = useState<IceAdmin[]>([]);
    const [editItem, setEditItem] = useState<IceAdmin | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<IceAdmin | null>(null);

    const backend = new ApiBackend();

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        const next = await backend.listIceAdmins();
        setAdmins(next);
        setLoading(false);
    };

    const handleAddClick = () => {
        setIsNew(true);
        setEditItem({ login: "", loginType: "phone" });
    };

    const handleEditClick = (item: IceAdmin) => () => {
        setIsNew(false);
        setEditItem({ ...item });
    };

    const handleSave = async () => {
        setEditItem(null);
        await fetchAdmins();
    };

    const handleDeleteOk = async () => {
        setLoading(true);
        await backend.deleteIceAdmin(confirmDelete.login);
        setConfirmDelete(null);
        await fetchAdmins();
    };

    const render = () => {
        const { classes } = props;

        return (<>
            <Grid container className={classes.root} spacing={2}>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader className={classes.cardHeader} title="ICE-admins (inloggning till ice.vasaloppet.se)" />
                        <CardContent>
                            {loading &&
                                <LinearProgress color="secondary" />
                            }
                            <TableContainer component={Paper}>
                                <Table size="small" aria-label="ice admins">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Login</StyledTableCell>
                                            <StyledTableCell>Typ</StyledTableCell>
                                            <StyledTableCell>Senaste inloggning</StyledTableCell>
                                            <StyledTableCell style={{ width: 90 }}></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {admins.map((item) =>
                                            <TableRow key={item.login}>
                                                <StyledTableCell>{item.login}</StyledTableCell>
                                                <StyledTableCell>{item.loginType}</StyledTableCell>
                                                <StyledTableCell>{item.lastLogin ? vasaloppetDateTimeGetterFormatter(item.lastLogin) : "-"}</StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <IconButton color="default" size="small" disabled={loading} onClick={handleEditClick(item)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton color="default" size="small" disabled={loading} onClick={() => setConfirmDelete(item)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </StyledTableCell>
                                            </TableRow>
                                        )}
                                        {!loading && admins.length === 0 &&
                                            <TableRow>
                                                <StyledTableCell colSpan={4}>Inga ICE-admins upplagda</StyledTableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                        <CardActions>
                            <Button size="small" color="secondary" disabled={loading} onClick={handleAddClick}>
                                Lägg till
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>
            {editItem &&
                <EditDialog
                    editItem={editItem}
                    isNew={isNew}
                    existingLogins={admins.map(a => a.login)}
                    onAbort={() => setEditItem(null)}
                    onSave={handleSave}
                />
            }
            {confirmDelete &&
                <Dialog disableEscapeKeyDown maxWidth="xs" open={true}>
                    <DialogTitle>Ta bort {confirmDelete.login}?</DialogTitle>
                    <DialogActions>
                        <Button variant="contained" color="primary" onClick={() => setConfirmDelete(null)}>Avbryt</Button>
                        <Button variant="contained" color="primary" onClick={handleDeleteOk}>Ja, ta bort</Button>
                    </DialogActions>
                </Dialog>
            }
        </>);
    };

    return render();
};

type EditDialogProps = {
    editItem: IceAdmin;
    isNew: boolean;
    existingLogins: string[];
    onAbort: () => void;
    onSave: () => void;
};

const EditDialog = withToaster((props: EditDialogProps & IToasterContext) => {
    const [login, setLogin] = useState<string>(props.editItem.login);
    const [loginType, setLoginType] = useState<IceAdminLoginType>(props.editItem.loginType || "phone");
    const [loading, setLoading] = useState(false);

    const backend = new ApiBackend();

    const handleLoginChange = (value: string) => {
        setLogin(value);
        // Autodetektera typ vid nyregistrering
        if (props.isNew) {
            setLoginType(value.indexOf("@") >= 0 ? "email" : "phone");
        }
    };

    const handleSave = async () => {
        const trimmed = (login || "").trim();
        if (props.isNew && props.existingLogins.some(l => l.toLowerCase() === trimmed.toLowerCase())) {
            props.showToast("Denna login finns redan", "error");
            return;
        }
        setLoading(true);
        const ok = await backend.addOrUpdateIceAdmin(trimmed, loginType);
        setLoading(false);
        if (ok) {
            props.onSave();
        } else {
            props.showToast("Någonting gick fel när vi försökte spara", "error");
        }
    };

    const canSave = !!(login && login.trim()) && !!loginType;

    return (
        <Dialog disableEscapeKeyDown fullWidth maxWidth="sm" open={true}>
            <DialogTitle>{props.isNew ? "Lägg till ICE-admin" : `Redigera ${props.editItem.login}`}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Login (telefonnummer eller e-post)"
                            InputLabelProps={{ shrink: true }}
                            value={login ?? ""}
                            disabled={!props.isNew}
                            onChange={(e) => handleLoginChange(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Typ av login"
                            InputLabelProps={{ shrink: true }}
                            value={loginType}
                            onChange={(e) => setLoginType(e.target.value as IceAdminLoginType)}
                        >
                            <MenuItem value="phone">phone</MenuItem>
                            <MenuItem value="email">email</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" variant="contained" disabled={loading || !canSave} onClick={handleSave}>Spara</Button>
                <Button color="secondary" variant="contained" style={{ marginLeft: 10 }} disabled={loading} onClick={props.onAbort}>Avbryt</Button>
            </DialogActions>
        </Dialog>
    );
});

const useStyles = ({ palette }: Theme) => createStyles({
    root: {},
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    }
});

const tableHeadStyles = ({ palette }: Theme) => createStyles({
    head: {
        background: palette.primary.main,
        color: palette.primary.contrastText,
    }
});

const StyledTableCell = withStyles(tableHeadStyles)(TableCell);

export default withStyles(useStyles)(ManageIceAdmins);
